import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/feature/EmptyState";
import SectionTitle from "@/components/feature/SectionTitle";
import usePageMeta from "@/hooks/usePageMeta";
import { categories, getHotNews, newsList, searchNews } from "@/utils/news";
import type { SortKey } from "@/types/news";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";

  const [input, setInput] = useState(query);
  const [categoryId, setCategoryId] = useState("");
  const [sort, setSort] = useState<SortKey>("time");
  const [showSuggest, setShowSuggest] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInput(query);
    setCategoryId("");
    setSort("time");
  }, [query]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const suggestions = useMemo(() => {
    const kw = input.trim().toLowerCase();
    if (!kw) return [];
    return newsList
      .filter((n) => `${n.title} ${n.tags.join(" ")}`.toLowerCase().includes(kw))
      .slice(0, 6);
  }, [input]);

  const results = useMemo(
    () => (query.trim() ? searchNews(query, categoryId || undefined, sort) : []),
    [query, categoryId, sort]
  );

  const hotNews = useMemo(() => getHotNews(4), []);

  usePageMeta({
    title: query ? `搜索：${query} - 吃瓜爆料` : "全站搜索 - 吃瓜爆料",
    description: query
      ? `在吃瓜爆料中搜索“${query}”的相关娱乐八卦与社会新闻热点。`
      : "输入关键词，快速查找吃瓜爆料中的娱乐八卦与社会新闻热点。",
    keywords: "站内搜索,娱乐八卦,社会新闻,吃瓜爆料",
  });

  const runSearch = (kw: string) => {
    const keyword = kw.trim();
    setShowSuggest(false);
    setParams(keyword ? { q: keyword } : {});
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="w-full px-4 md:px-8 py-6">
        <Breadcrumb items={[{ label: "搜索" }]} />

        <div className="rounded-lg bg-primary-50 border border-primary-100 p-5 md:p-7 mb-6">
          <h1 className="text-2xl md:text-3xl font-black text-foreground-950">全站搜索</h1>
          <p className="text-sm text-foreground-600 mt-2">输入关键词，快速找到你关心的爆料与热点。</p>

          <div ref={boxRef} className="relative mt-5 max-w-2xl">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                runSearch(input);
              }}
              className="flex flex-col sm:flex-row gap-2"
            >
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    setShowSuggest(true);
                  }}
                  onFocus={() => setShowSuggest(true)}
                  placeholder="试试搜索“巡演”“地铁”“转型”"
                  className="w-full h-11 pl-11 pr-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                type="submit"
                className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 cursor-pointer"
              >
                搜索
              </button>
            </form>

            {showSuggest && suggestions.length > 0 && (
              <div className="absolute z-30 left-0 right-0 mt-2 rounded-md bg-background-50 border border-background-200 overflow-hidden">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setInput(s.title);
                      runSearch(s.title);
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-search-line text-foreground-400" />
                    <span className="truncate">{s.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {query.trim() ? (
          <>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-5">
              <p className="text-sm text-foreground-600">
                搜索 “<strong className="text-primary-600">{query}</strong>” ，共找到{" "}
                <strong className="text-primary-600">{results.length}</strong> 条结果
              </p>

              <div className="flex items-center gap-3 flex-wrap">
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  className="h-9 px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-700 outline-none focus:border-primary-400 cursor-pointer"
                >
                  <option value="">全部分类</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <div className="flex items-center bg-background-100 rounded-full p-1">
                  {(["time", "views"] as SortKey[]).map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSort(key)}
                      className={`h-7 px-3.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                        sort === key ? "bg-background-50 text-primary-600" : "text-foreground-600"
                      }`}
                    >
                      {key === "time" ? "最新发布" : "按热度"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {results.length > 0 ? (
              <div className="flex flex-col gap-3">
                {results.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-emotion-sad-line"
                title="未找到相关内容"
                description="换个关键词试试，或者看看下面这些正在被热议的爆料。"
              />
            )}
          </>
        ) : (
          <EmptyState
            icon="ri-search-eye-line"
            title="输入关键词开始搜索"
            description="目前为前端演示搜索，支持按标题、标签与子分类匹配。"
          />
        )}

        {(results.length === 0 || !query.trim()) && (
          <section className="mt-10">
            <SectionTitle title="热门爆料推荐" subtitle="大家最近都在看这些" icon="ri-fire-line" />
            <div className="flex flex-col gap-3">
              {hotNews.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}